import { useEffect, useRef } from "react";
import { useAtom, useSetAtom } from "jotai";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router";
import { useAuthStore } from "@/store/auth";
import { getActiveBuildTask, getBuildRouteResult } from "@/api/routes";
import { invalidateMapSurfaces } from "@/features/map/lib/invalidateMapSurfaces";
import { toastWarning } from "@/lib/toast.tsx";
import {
  buildResultShownByNotificationRecently,
  payloadFromUnassignedOrders,
  showBuildFailureToast,
  showBuildResultToast,
  singleBuildDate,
} from "@/features/routes/lib/buildResultToast";
import { parseBuildWarnings } from "@/features/routes/lib/buildWarnings";
import { builtRoutesListUrl } from "@/features/routes/lib/builtRoutesListUrl";
import {
  buildRouteTaskAtom,
  builtRoutesAtom,
  builtRoutesByDateAtom,
  unassignedBuildOrdersAtom,
} from "@/store/buildRouteTask";

/**
 * Global hook that drives the build-route background task.
 * Must be mounted once at the AppShell level.
 */
export function useBuildRoutePoller(): void {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [task, setTask] = useAtom(buildRouteTaskAtom);
  const setUnassignedOrders = useSetAtom(unassignedBuildOrdersAtom);
  const setBuiltRoutes = useSetAtom(builtRoutesAtom);
  const setBuiltRoutesByDate = useSetAtom(builtRoutesByDateAtom);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);

  // Tracks the last task id whose terminal "completed" outcome was already
  // handled, so the completion side-effects (toasts, invalidations,
  // setBuiltRoutes, navigate) run exactly once per task — even if the task atom
  // is re-set to the same completed id by the server still reporting it
  // (BUG-178).
  const handledCompletedTaskIdRef = useRef<string | null>(null);

  const pollingQuery = useQuery({
    queryKey: ["buildRouteResult", task?.taskId],
    queryFn: () => getBuildRouteResult(task!.taskId),
    // Do not poll the build result while logged out (BUG-175).
    enabled: task !== null && isAuthenticated,
    refetchInterval: (query) => {
      const status = query.state.data?.status;
      return status === "completed" || status === "failed" ? false : 2000;
    },
    retry: false,
  });

  useEffect(() => {
    if (task === null) return;

    // 404 means task result expired from Redis (task finished before first poll)
    if (pollingQuery.isError) {
      invalidateMapSurfaces(queryClient);
      // Result expired from Redis: real outcome is unknown, so do not show a
      // false green success. Use a neutral warning instead — unless the
      // `routes_built` notification already toasted the REAL result (BUG-676).
      if (!buildResultShownByNotificationRecently()) {
        toastWarning(t("toast.buildResultUnavailable"));
      }
      setTask(null);
      return;
    }

    if (!pollingQuery.data) return;

    if (
      pollingQuery.data.status === "running" &&
      pollingQuery.data.progress_pct != null &&
      pollingQuery.data.progress_pct !== task.progressPct
    ) {
      setTask((prev) =>
        prev ? { ...prev, progressPct: pollingQuery.data.progress_pct } : null,
      );
    }

    if (pollingQuery.data.status === "completed") {
      // Handle a given task's completion only once. If the server keeps
      // reporting the same completed task_id (or the atom is re-set to it), the
      // redirect/toasts must not fire again (BUG-178).
      if (handledCompletedTaskIdRef.current === task.taskId) {
        setTask(null);
        return;
      }
      handledCompletedTaskIdRef.current = task.taskId;

      const routes = pollingQuery.data.result?.routes ?? [];
      const unassigned = pollingQuery.data.result?.unassigned ?? [];
      const byDate = pollingQuery.data.result?.by_date ?? [];
      // BUG-334: refresh the Orders-map pins, filter counts and KPI strip (all
      // fed by ["orders"]) plus the Routes surfaces the build populates.
      invalidateMapSurfaces(queryClient);

      // BUG-676: ONE toast per build, shared with the `routes_built`
      // notification — see `buildResultToast.tsx`. Styled by outcome (nothing
      // created → error, some orders dropped → warning, all placed → success),
      // with the dropped orders grouped by reason and then by address.
      showBuildResultToast(
        {
          routesCount: routes.length,
          unassigned: payloadFromUnassignedOrders(unassigned),
          // BUG-691: e.g. routes planned on a straight-line estimate.
          warnings: parseBuildWarnings(pollingQuery.data.result?.warnings),
          // BUG-694: names the night of a next-night window («ночь на 23 окт.»).
          routeDate: singleBuildDate(routes, byDate),
        },
        "poller",
        t,
      );

      setTask(null);
      if (unassigned.length > 0) {
        setUnassignedOrders(unassigned);
      }
      if (routes.length > 0) {
        // Populate the green build summary so it renders on the routes list,
        // then redirect to the list itself (not the first route's detail) so
        // the dispatcher sees every created route at a glance — with the period
        // set to the built dates, or a remembered period could hide them all.
        setBuiltRoutes(routes);
        setBuiltRoutesByDate(byDate);
        void navigate(builtRoutesListUrl(routes, byDate));
      }
    }

    if (pollingQuery.data.status === "failed") {
      // BUG-679: ONE failure toast per build, shared with the ROUTES_BUILD_FAILED
      // notification and worded by the SAME localizer (code → params → reason),
      // so the raw English `error` («CP Solver fail») never becomes the body: an
      // INTERNAL_ERROR renders the localized generic sentence, a registry code
      // its own sentence with figures. See `buildResultToast.tsx`.
      showBuildFailureToast(
        {
          code: pollingQuery.data.code ?? null,
          params: pollingQuery.data.params ?? null,
          reason: pollingQuery.data.error ?? "",
        },
        "poller",
      );
      setTask(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pollingQuery.data, pollingQuery.isError]);

  // Restore atom state after page refresh: if a build task is still running on
  // the server but the atom was reset (null), re-enable polling by setting the
  // atom. Skip entirely while logged out so the /login page fires no
  // authenticated API calls (BUG-175).
  useEffect(() => {
    if (!isAuthenticated) return;
    getActiveBuildTask()
      .then((data) => {
        if (data.task_id && task === null) {
          setTask({ taskId: data.task_id, progressPct: null });
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);
}
